import { getAuthToken } from './auth/tokenStorage';
import { API_URL } from '../config';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { createLogger } from '../utils/logger';

const logger = createLogger('twoFactor');
export interface TwoFactorSetupResult {
  success: boolean;
  message?: string;
  phoneNumber?: string;
}

export interface TwoFactorVerifyResult {
  success: boolean;
  message?: string;
  token?: string;
}

class TwoFactorService {
  private static PHONE_STORAGE_KEY = '@user_phone_number';

  /**
   * Send 2FA verification code to user's phone
   */
  async sendVerificationCode(phoneNumber: string): Promise<TwoFactorSetupResult> {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_URL}/auth/send-2fa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const result = await response.json();

      if (response.ok) {
        // Store phone number locally for later reference
        await AsyncStorage.setItem(
          TwoFactorService.PHONE_STORAGE_KEY,
          phoneNumber
        );

        return {
          success: true,
          message: result.message || 'Verification code sent successfully',
          phoneNumber,
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to send verification code',
        };
      }
    } catch (error) {
      logger.error('Failed to send verification code:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.',
      };
    }
  }

  /**
   * Verify the 2FA code entered by the user
   */
  async verifyCode(
    phoneNumber: string,
    code: string
  ): Promise<TwoFactorVerifyResult> {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_URL}/auth/verify-2fa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ phoneNumber, code }),
      });

      const result = await response.json();

      if (response.ok) {
        // Store 2FA completion status
        await AsyncStorage.setItem('@2fa_verified', 'true');

        return {
          success: true,
          message: result.message || 'Verification successful',
          token: result.token,
        };
      } else {
        return {
          success: false,
          message: result.message || 'Invalid verification code',
        };
      }
    } catch (error) {
      logger.error('Failed to verify code:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.',
      };
    }
  }

  /**
   * Resend verification code
   */
  async resendCode(phoneNumber: string): Promise<TwoFactorSetupResult> {
    return await this.sendVerificationCode(phoneNumber);
  }

  /**
   * Get stored phone number
   */
  async getStoredPhoneNumber(): Promise<string | null> {
    try {
      return await AsyncStorage.getItem(TwoFactorService.PHONE_STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to get stored phone number:', error);
      return null;
    }
  }

  /**
   * Check if user has completed 2FA setup
   */
  async has2FASetup(): Promise<boolean> {
    try {
      const phoneNumber = await this.getStoredPhoneNumber();
      return phoneNumber !== null;
    } catch (error) {
      logger.error('Failed to check 2FA setup:', error);
      return false;
    }
  }

  /**
   * Clear 2FA data (logout)
   */
  async clear2FAData(): Promise<void> {
    try {
      await AsyncStorage.removeItem(TwoFactorService.PHONE_STORAGE_KEY);
      await AsyncStorage.removeItem('@2fa_verified');
    } catch (error) {
      logger.error('Failed to clear 2FA data:', error);
    }
  }

  /**
   * Format phone number for display (mask middle digits)
   */
  formatPhoneForDisplay(phoneNumber: string): string {
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 10) {
      // Format: (XXX) XXX-XXXX → (XXX) ***-**XX
      return `(${cleaned.substring(0, 3)}) ***-**${cleaned.substring(8)}`;
    } else if (cleaned.length === 11) {
      // Format: +1 XXX-XXX-XXXX → +1 XXX-***-**XX
      return `+${cleaned.substring(0, 1)} ${cleaned.substring(1, 4)}-***-**${cleaned.substring(9)}`;
    }

    // Fallback: Just mask middle characters
    const visible = 4;
    const masked = phoneNumber.substring(visible, phoneNumber.length - visible).replace(/./g, '*');
    return phoneNumber.substring(0, visible) + masked + phoneNumber.substring(phoneNumber.length - visible);
  }
}

export default new TwoFactorService();
